import { Alert, Button, Card, Col, DatePicker, Descriptions, Row, Space, Statistic, Table, Tag } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type TwinSignal = {
  key: string;
  label: string;
  value: number;
  path?: string | null;
};

type TwinPayload = {
  student_id: string;
  as_of: string;
  advisory: true;
  identity: { code: string; full_name: string; status: string };
  signals: TwinSignal[];
  notes: string[];
};

export default function StudentDigitalTwinPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const { id = '' } = useParams();
  const [asOf, setAsOf] = useState<Dayjs>(() => dayjs());

  const query = useQuery({
    queryKey: ['education-student-twin', id, asOf.format('YYYY-MM-DD')],
    enabled: Boolean(id),
    queryFn: async () =>
      (
        await api.get<TwinPayload>(`/education/students/${id}/digital-twin`, {
          params: { as_of: asOf.format('YYYY-MM-DD') },
        })
      ).data,
  });

  const twin = query.data;
  const backToStudent = () => navigate(`/education/students/${id}`);

  return (
    <EducationPageChrome
      title={t('studentTwin')}
      description={t('studentTwinIntro')}
      tags={twin ? <Tag>{`${t('studentTwinAsOf')}: ${twin.as_of}`}</Tag> : undefined}
      extra={
        <Space wrap>
          <DatePicker value={asOf} allowClear={false} onChange={(v) => v && setAsOf(v)} />
          <Button onClick={backToStudent}>{t('studentTwinBack')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Alert type="info" showIcon message={t('studentTwinAdvisoryNote')} />
        {twin?.notes?.length ? (
          <Alert type="warning" showIcon message={twin.notes.join(' ')} />
        ) : null}
        {query.isError ? <Alert type="error" showIcon message={t('loadFailed', { defaultValue: 'Failed to load' })} /> : null}

        <Card size="small" title={t('studentTwinIdentity')} loading={query.isLoading}>
          <Descriptions size="small" column={{ xs: 1, md: 3 }}>
            <Descriptions.Item label={t('studentTwinIdentity')}>{twin?.identity.full_name || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('studentTwinCode')}>{twin?.identity.code || '—'}</Descriptions.Item>
            <Descriptions.Item label={t('studentTwinStatus')}>{twin?.identity.status || '—'}</Descriptions.Item>
          </Descriptions>
        </Card>

        <Row gutter={[16, 16]}>
          {(twin?.signals ?? []).map((s) => (
            <Col xs={12} md={6} key={s.key}>
              <Card size="small" loading={query.isLoading}>
                <Statistic title={s.label} value={s.value} />
              </Card>
            </Col>
          ))}
        </Row>

        <Card size="small" title={t('studentTwinSignals')}>
          <Table
            rowKey="key"
            size="small"
            loading={query.isLoading}
            pagination={false}
            locale={{ emptyText: t('studentTwinNoSignals') }}
            dataSource={twin?.signals ?? []}
            columns={[
              { title: t('studentTwinSignal'), dataIndex: 'label' },
              { title: t('studentTwinValue'), dataIndex: 'value', width: 120 },
              {
                title: t('actions'),
                width: 140,
                render: (_: unknown, row: TwinSignal) =>
                  row.path ? (
                    <Button size="small" type="link" onClick={() => navigate(row.path as string)}>
                      {t('studentTwinOpenSource')}
                    </Button>
                  ) : (
                    '—'
                  ),
              },
            ]}
          />
        </Card>
      </Space>
    </EducationPageChrome>
  );
}
